import { Attributes, type ProductTeaser } from '~/components/Product/types/product';
import { useFilters } from '~/composables/custom/useFilters';
import { useBFF } from '~/composables/useBFF/useBFF';

const MAX_ITEMS_PER_CAROUSEL_DESKTOP = import.meta.client ? 20 : 6;
const MAX_ITEMS_PER_CAROUSEL_MOBILE = import.meta.client ? 20 : 2;

export const useTeaserSlider = (categories: string[]) => {
  const { isDesktop } = useDevice();
  const { $products, $recommend } = useBFF();
  const { getFilters } = useFilters();

  const MAX_ITEMS_PER_CAROUSEL = isDesktop ? MAX_ITEMS_PER_CAROUSEL_DESKTOP : MAX_ITEMS_PER_CAROUSEL_MOBILE;

  const loading = ref(false);
  const products = useState<ProductTeaser[]>(`useTeaserSlider-products-${categories?.toString()}`, () => shallowRef([]));

  const fetchProducts = async (categories: string[], operator: string, byAttributes: any) => {
    loading.value = true;
    try {
      const { data } = await $products.getProducts({
        categories: categories,
        attributeFilterNames: [
          Attributes.COLOR_CODE,
          Attributes.DETAIL_NAME,
          Attributes.COLOR,
          Attributes.SIZE,
          Attributes.SIZE_ORDER,
          Attributes.DISCOUNT_SALE_VALUE,
        ],
        filterAndSortBy: getFilters(byAttributes),
        withVariants: false,
        operator,
        limit: MAX_ITEMS_PER_CAROUSEL,
      });

      products.value = data?.items ?? [];
    }
    finally {
      loading.value = false;
    }
  };

  const fetchRecommendations = async (type: string, productId: string | null | undefined, category: string | null | undefined = null) => {
    loading.value = true;

    const cookie = useCookie<string | undefined>('_lb');

    try {
      const { data } = await $recommend.getRecommendations({
        attributeFilterNames: [
          Attributes.COLOR_CODE,
          Attributes.DETAIL_NAME,
          Attributes.COLOR,
          Attributes.SIZE,
          Attributes.SIZE_ORDER,
          Attributes.DISCOUNT_SALE_VALUE,
        ],
        withVariants: true,
        ...(category ? { categories: [category] } : null),
      }, {
        userId: cookie.value,
        type,
        productId,
      });

      products.value = data?.items ?? [];
    }
    finally {
      loading.value = false;
    }
  };

  return {
    loading: computed(() => loading.value),
    products: computed(() => products.value),

    fetchProducts,
    fetchRecommendations,
  };
};
